import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileBadge, Edit, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import StatusBadge from "../StatusBadge";
import { downloadCertificate } from "../../utils/pdfGenerator";

export default function ApplicationHeader({
  application,
  user,
  canFarmerModify,
  onEditClick,
  onDeleteClick,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <header className="bg-white border-b border-stone-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="hover:bg-stone-100"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("application.back")}
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-manrope font-bold text-emerald-700 tracking-tight">
                {t("application.details")}
              </h1>
              {application.application_id && (
                <span className="px-3 py-1 bg-blue-100 text-blue-700 font-mono font-bold rounded-md text-sm">
                  {application.application_id}
                </span>
              )}
            </div>
            <p className="text-sm font-mono text-slate-500">
              {t("table.surveyNumber")}: {application.land_survey_number}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Farmer Edit/Delete Actions */}
            {canFarmerModify && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={onEditClick}
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  data-testid="edit-application-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  {t("applicationDetail.editApplication")}
                </Button>
                <Button
                  variant="outline"
                  onClick={onDeleteClick}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="delete-application-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t("applicationDetail.deleteApplication")}
                </Button>
              </div>
            )}

            {/* Status and Assigned Official Info */}
            <div className="flex flex-col items-end">
              <StatusBadge status={application.status} />
              {application.assigned_official_name && (
                <div className="mt-1 text-right" data-testid="assigned-official">
                  <p className="text-xs text-slate-500">
                    {t("status.assignedOfficial")}:
                  </p>
                  <p className="text-sm font-medium text-emerald-800">
                    {application.assigned_official_name}
                    {application.assigned_official_designation && (
                      <span className="text-slate-600 font-normal">
                        {" "}
                        ({t(`designation.${application.assigned_official_designation}`)})
                      </span>
                    )}
                  </p>
                </div>
              )}
              {!application.assigned_official_name &&
                application.status !== "submitted" && (
                  <p className="mt-1 text-xs text-slate-400">
                    {t("status.notAssigned")}
                  </p>
                )}
            </div>
            {application.status === "approved" && (
              <Button
                onClick={() => downloadCertificate(application, user?.name)}
                className="bg-emerald-800 hover:bg-emerald-900 text-white"
                data-testid="download-certificate"
              >
                <FileBadge className="w-4 h-4 mr-2" />
                {t("application.downloadCertificate")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
